import { Autocomplete, type AutocompleteProps, TextField } from '@mui/material'
import { get, type PropertyPath, set } from 'lodash'
import { useMemo } from 'react'
import { useFilterOptionsQuery } from '@/features/search/api'
import { useSearch } from '@/features/search/hooks/useSearch'
import { useAppSelector } from '@/redux/hooks'
import { MapStateToFlat } from '@/features/search/maps/searchState'
import type {
  FilterOptionsWithRequirement,
  PropertyStringPath,
  SearchFilterState,
  SelectOptionFieldNamesOfRequirements,
} from '@/features/search/types'

const EMPTY_ARRAY: readonly never[] = []

type ACProps = AutocompleteProps<string, boolean, boolean, boolean, 'div'>
interface Props extends Omit<ACProps, 'renderInput' | 'options'> {
  label: string
  fieldName: SelectOptionFieldNamesOfRequirements
  fieldPath: PropertyStringPath<SearchFilterState> & PropertyPath
  onChange?: () => void
  requirementOverride?: () => boolean
}

export const FilterRequirementsAutocomplete = (props: Props) => {
  const {
    fieldName,
    fieldPath,
    label,
    multiple = true,
    requirementOverride,
    ...restProps
  } = props
  const { data, ...filterRequest } = useFilterOptionsQuery(undefined, {
    selectFromResult: ({ data, ...rest }) => {
      if (data?.[fieldName] && Array.isArray(data[fieldName])) {
        if (Array.isArray(data[fieldName]))
          return {
            data: data[fieldName] satisfies FilterOptionsWithRequirement,
            ...rest,
          }
      }
      return { data: EMPTY_ARRAY, ...rest }
    },
  })

  const {
    selectors,
    updateNext,
  } = useSearch()

  const nextSearchOptions = useAppSelector(selectors.selectNextSearchParameters)
  const bypassedFieldRequirements = useAppSelector(selectors.selectBypassedFieldRequirements)

  const { validOptions = [], allOptions = [] } = useMemo(
    function getOptions() {
      //Set used to de-duplicate values
      const valid = new Set<string>()
      const invalid = new Set<string>()
      //flat state is easier to navigate for this purpose
      const flatState = MapStateToFlat(nextSearchOptions)

      data.forEach((option: FilterOptionsWithRequirement) => {
        const { requires, descriptions } = option

        const isValid =
          bypassedFieldRequirements[fieldName] ||
          Object.entries(requires).some(([fieldName, requiredValue]) => {
            const currentValue = flatState[fieldName as keyof typeof flatState]
            const isOverride = requirementOverride?.()

            const isValid = validateValue(requiredValue as string, currentValue)

            return isValid || isOverride
          })

        if (!Array.isArray(descriptions)) {
          return
        }

        if (isValid) {
          descriptions.forEach((option: string) => { valid.add(option) })
        } else {
          descriptions.forEach((option: string) => { invalid.add(option) })
        }
      })

      const ascendSort = (a: string, b: string) => (a < b ? -1 : 1)

      const validOptions = Array.from(valid).sort(ascendSort) ?? []
      const invalidOptions = Array.from(invalid).sort(ascendSort) ?? []
      const allOptions = Array.from(
        new Set([...validOptions, ...invalidOptions]),
      )

      return {
        validOptions,
        invalidOptions,
        allOptions,
      }
    },
    [nextSearchOptions, bypassedFieldRequirements, fieldName, data, requirementOverride],
  )

  const handleChange: ACProps['onChange'] = (
    _event,
    value,
    _reason,
    _details,
  ) => {
    updateNext(set({}, fieldPath, value))
  }

  const value =
    (get(nextSearchOptions, fieldPath) ?? []).filter((opt: string) =>
      validOptions.includes(opt),
    ) ?? []

  return (
    <Autocomplete
      fullWidth
      options={allOptions}
      getOptionDisabled={(option) => validOptions.indexOf(option) === -1}
      loading={filterRequest.isLoading}
      multiple={multiple}
      value={value ?? []}
      onChange={handleChange}
      renderInput={(params) => <TextField {...params} label={label} />}
      {...restProps}
    />
  )
}

const validateValue = (requirement: unknown, value: unknown): boolean => {
  if (requirement === value) return true
  if (Array.isArray(value) && value.includes(requirement)) return true
  return false
}
